import * as styles from "./ProfilerToolbar.css";

import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { refreshBridgeStyles } from "@frontend/bridge/BridgeContent";
import { CopyButton } from "@frontend/components/atoms/CopyButton";
import { Icon } from "@frontend/components/atoms/Icon";
import { Loader } from "@frontend/components/atoms/Loader";
import { Popover, PopoverContent } from "@frontend/components/atoms/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";

import { type Profile, profilerApi } from "../api";
import { asNumber, asRecord, asString } from "../api/plain";
import { untilFound } from "../api/retry";
import { ProfilerFolded } from "./ProfilerFolded";
import type { ProfilerToolbarDataProps } from "./ProfilerToolbar.schema";
import type { WindowSlot } from "./ProfilerWindows";
import { installAjaxInterception, useAjaxRequests } from "./ajaxRequests";
import { useCollapsed, useOpenMode } from "./preferences";
import { useDraggablePosition } from "./useDraggablePosition";

type Counters = {
  duration: number;
  memory: number;
  queries: number;
  // Distinct statements: fewer than queries means some ran more than once
  grouped: number;
  queryTime: number;
  errors: number;
  warnings: number;
};

const ProfilerWindows = lazy(() =>
  import("./ProfilerWindows").then((module) => {
    refreshBridgeStyles();

    return module;
  }),
);

// The profile is written at kernel.terminate, after the response left
// through fastcgi_finish_request, so a page served from cache can ask for
// it before it exists.
const SAVE_DELAYS = [300, 600, 1200, 2400];

export function ProfilerToolbar({
  token,
  traceId,
}: ProfilerToolbarDataProps & { token: string }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [counters, setCounters] = useState<Counters | null>(null);
  const [failed, setFailed] = useState(false);
  const [slots, setSlots] = useState<WindowSlot[]>([]);

  const [openMode] = useOpenMode();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => setOpen((value) => !value), []);

  const { position, settling, onPointerDown, measure } = useDraggablePosition(
    buttonRef,
    styles.BUTTON_ESTIMATE,
    toggle,
  );
  const [collapsed, setCollapsed] = useCollapsed();

  const ajax = useAjaxRequests();

  const failedAjax = ajax.filter(
    (request) => null !== request.status && request.status >= 400,
  ).length;

  useEffect(() => {
    installAjaxInterception();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const options = { signal: controller.signal };

    untilFound(
      () => profilerApi.fetchProfile(token, options),
      SAVE_DELAYS,
      controller.signal,
    )
      .then(setProfile)
      .catch(() => {
        // An unmount aborts the fetch, that is not a failure
        if (!controller.signal.aborted) {
          setFailed(true);
        }
      });

    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!open || counters || !profile) {
      return;
    }

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const fetch = (name: string): Promise<Record<string, unknown>> =>
      profile.collectors.some((collector) => collector.name === name)
        ? profilerApi.fetchCollector(token, name, options).then((c) => c.data)
        : Promise.resolve({});

    Promise.all([fetch("time"), fetch("memory"), fetch("db"), fetch("logger")])
      .then(([time, memory, db, logger]) => {
        const priorities = asRecord(logger.priorities);
        const count = (names: string[]) =>
          Object.values(priorities).reduce<number>((total, entry) => {
            const row = asRecord(entry);

            return names.includes(asString(row.name))
              ? total + asNumber(row.count)
              : total;
          }, 0);

        setCounters({
          duration: asNumber(time.duration),
          memory: asNumber(memory.memory),
          queries: asNumber(db.queryCount),
          grouped: asNumber(db.groupedQueryCount),
          queryTime: asNumber(db.time) * 1000,
          errors: count(["EMERGENCY", "ALERT", "CRITICAL", "ERROR"]),
          warnings: count(["WARNING"]),
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailed(true);
        }
      });

    return () => controller.abort();
  }, [open, counters, profile, token]);

  const badges = new Map(
    profile?.collectors.map((collector) => [collector.name, collector.badge]),
  );
  const available = (name: string) =>
    profile?.collectors.some(
      (collector) => collector.name === name && collector.available,
    ) ?? false;
  const status = profile?.summary.statusCode ?? null;
  const hasException = "1" === badges.get("exception");
  const repeated =
    counters && counters.grouped > 0
      ? Math.max(0, counters.queries - counters.grouped)
      : 0;
  // What deserves a mark on the pill without opening it
  const alerts = (hasException ? 1 : 0) + failedAjax;
  const tone =
    null === status
      ? undefined
      : status >= 500 || hasException
        ? "error"
        : status >= 400
          ? "warning"
          : "success";

  const openPanel = (target: string, forToken = token) => {
    setSlots((current) => {
      const owner = current.find((slot) =>
        slot.profiles.some((profile) => profile.token === forToken),
      );

      if (owner) {
        return current.map((slot) =>
          slot === owner
            ? {
                ...slot,
                active: forToken,
                profiles: slot.profiles.map((profile) =>
                  profile.token === forToken
                    ? { ...profile, panel: target }
                    : profile,
                ),
              }
            : slot,
        );
      }

      const opened = { token: forToken, panel: target };

      if ("tab" === openMode && current.length > 0) {
        return current.map((slot, index) =>
          0 === index
            ? {
                ...slot,
                active: forToken,
                profiles: [...slot.profiles, opened],
              }
            : slot,
        );
      }

      const id = 1 + Math.max(0, ...current.map((slot) => slot.id));

      return [...current, { id, profiles: [opened], active: forToken }];
    });
    setOpen(false);
  };

  const closeProfile = (slotId: number, closed: string) => {
    setSlots((current) =>
      current.flatMap((slot) => {
        if (slot.id !== slotId) {
          return [slot];
        }

        const profiles = slot.profiles.filter(
          (profile) => profile.token !== closed,
        );

        if (0 === profiles.length) {
          return [];
        }

        return [
          {
            ...slot,
            profiles,
            active: profiles.some((profile) => profile.token === slot.active)
              ? slot.active
              : profiles[profiles.length - 1].token,
          },
        ];
      }),
    );
  };

  const windows = slots.length > 0 && (
    <Suspense fallback={null}>
      <ProfilerWindows
        slots={slots}
        pageToken={token}
        onSelect={(forToken, panel) => openPanel(panel, forToken)}
        onOpen={(forToken, panel) => openPanel(panel, forToken)}
        onActivate={(slotId, active) =>
          setSlots((current) =>
            current.map((slot) =>
              slot.id === slotId ? { ...slot, active } : slot,
            ),
          )
        }
        onCloseProfile={closeProfile}
        onCloseSlot={(slotId) =>
          setSlots((current) => current.filter((slot) => slot.id !== slotId))
        }
      />
    </Suspense>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        {collapsed ? (
          <ProfilerFolded
            ref={buttonRef}
            position={position}
            size={measure()}
            tone={tone}
            label={badges.get("time") ?? (failed ? "?" : String(status ?? "…"))}
            open={open}
            onClick={toggle}
            onExpand={() => {
              setOpen(false);
              setCollapsed(false);
            }}
          />
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  ref={buttonRef}
                  type="button"
                  className={styles.button}
                  style={{ left: position.x, top: position.y }}
                  data-settling={settling || undefined}
                  data-tone={tone}
                  onPointerDown={onPointerDown}
                  onDoubleClick={() => {
                    setOpen(false);
                    setCollapsed(true);
                  }}
                  aria-label="Profiler"
                  aria-expanded={open}
                />
              }
            >
              {null === status && !failed ? (
                <Loader variant="dots" size="xs" label="Loading the profile" />
              ) : (
                <span className={styles.status}>{failed ? "?" : status}</span>
              )}
              {badges.get("time") && (
                <span className={styles.duration}>{badges.get("time")}</span>
              )}
              {alerts > 0 && (
                // Remounted on each change so the pulse plays again
                <span key={alerts} className={styles.alert}>
                  {alerts}
                </span>
              )}
            </TooltipTrigger>
            <TooltipContent side="top">
              Profiler: click for the figures, drag to move, double-click to
              fold
            </TooltipContent>
          </Tooltip>
        )}
        <PopoverContent anchor={buttonRef} side="top" align="end">
          <div className={styles.panel}>
            {failed && (
              <span className={styles.muted}>
                The profile is not available.
              </span>
            )}
            {profile && (
              <div className={styles.title}>
                <strong>{profile.summary.method}</strong>
                <span>{new URL(profile.summary.url).pathname}</span>
              </div>
            )}
            {profile?.redirect && (
              <div className={styles.counters}>
                <Counter
                  label="Redirected from"
                  value={`${profile.redirect.method} ${profile.redirect.route} (${profile.redirect.statusCode})`}
                  onClick={() =>
                    profile.redirect?.token &&
                    openPanel("request", profile.redirect.token)
                  }
                />
              </div>
            )}
            {counters ? (
              <div className={styles.counters}>
                {available("time") && (
                  <Counter
                    label="Time"
                    value={`${counters.duration.toFixed(0)} ms`}
                    onClick={() => openPanel("time")}
                  />
                )}
                <Counter
                  label="Memory"
                  value={`${(counters.memory / 1048576).toFixed(1)} MiB`}
                  onClick={() =>
                    openPanel(available("time") ? "time" : "memory")
                  }
                />
                <Counter
                  label="Queries"
                  value={`${counters.queries} in ${counters.queryTime.toFixed(2)} ms${
                    repeated > 0 ? `, ${repeated} repeated` : ""
                  }`}
                  warning={repeated > 0}
                  onClick={() => openPanel("db")}
                />
                {available("logger") && (
                  <Counter
                    label="Logs"
                    value={`${counters.errors} errors, ${counters.warnings} warnings`}
                    error={counters.errors > 0}
                    onClick={() => openPanel("logger")}
                  />
                )}
                {hasException && (
                  <Counter
                    label="Exception"
                    value="thrown"
                    error
                    onClick={() => openPanel("exception")}
                  />
                )}
                {badges.get("security") && (
                  <Counter
                    label="User"
                    value={badges.get("security") ?? ""}
                    onClick={() => openPanel("security")}
                  />
                )}
                {traceId && (
                  <Counter
                    label="Trace"
                    value={traceId}
                    onClick={() => openPanel("trace")}
                  />
                )}
                {ajax.length > 0 && (
                  <Counter
                    label="Ajax"
                    value={`${ajax.length} request${1 === ajax.length ? "" : "s"}${
                      failedAjax > 0 ? `, ${failedAjax} failed` : ""
                    }`}
                    error={failedAjax > 0}
                    onClick={() => openPanel("ajax")}
                  />
                )}
              </div>
            ) : (
              !failed && <span className={styles.muted}>Loading…</span>
            )}
            {profile && (
              <div className={styles.copies}>
                <CopyButton value={token} size="sm" variant="ghost">
                  Copy the token
                </CopyButton>
                {traceId && (
                  <CopyButton value={traceId} size="sm" variant="ghost">
                    Copy the trace id
                  </CopyButton>
                )}
                <CopyButton
                  value={`${window.location.origin}/profiler/${token}/request`}
                  size="sm"
                  variant="ghost"
                >
                  Copy the link
                </CopyButton>
              </div>
            )}
            <div className={styles.links}>
              <a
                className={styles.link}
                href={`/profiler/${token}/request`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in the profiler
                <Icon name="ExternalLink" size="sm" />
              </a>
              {traceId && (
                <a
                  className={styles.link}
                  href={`/profiler/${token}/trace`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the trace
                  <Icon name="ExternalLink" size="sm" />
                </a>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {windows}
    </>
  );
}

function Counter({
  label,
  value,
  error = false,
  warning = false,
  onClick,
}: {
  label: string;
  value: string;
  error?: boolean;
  warning?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={styles.counter} onClick={onClick}>
      <span className={styles.label}>{label}</span>
      <span
        className={`${styles.value} ${
          error ? styles.valueError : warning ? styles.valueWarning : ""
        }`}
      >
        {value}
      </span>
      <Icon name="AppWindow" size="sm" className={styles.chevron} />
    </button>
  );
}
